import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentStatusModal = ({ 
  isOpen, 
  status, 
  onClose, 
  onRetry, 
  bookingReference,
  transactionId,
  amount 
}) => {
  if (!isOpen) return null;

  const formatIndianNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: 'CheckCircle',
          iconColor: 'text-success',
          bgColor: 'bg-success/10',
          title: 'Payment Successful!',
          message: 'Your booking has been confirmed successfully.',
          showActions: true
        };
      case 'failed':
        return {
          icon: 'XCircle',
          iconColor: 'text-error',
          bgColor: 'bg-error/10',
          title: 'Payment Failed',
          message: 'We encountered an issue processing your payment. Please try again.',
          showActions: true
        };
      case 'processing':
        return {
          icon: 'Loader2',
          iconColor: 'text-secondary',
          bgColor: 'bg-secondary/10',
          title: 'Processing Payment...',
          message: 'Please wait while we process your payment. Do not close this window.',
          showActions: false
        };
      default:
        return {
          icon: 'AlertCircle',
          iconColor: 'text-warning',
          bgColor: 'bg-warning/10',
          title: 'Payment Status Unknown',
          message: 'We are verifying your payment status. Please wait.',
          showActions: false
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1010 p-4">
      <div className="bg-card rounded-lg border border-border max-w-md w-full shadow-prominent">
        <div className="p-6">
          {/* Status Icon */}
          <div className={`w-16 h-16 ${config.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Icon 
              name={config.icon} 
              size={32} 
              className={`${config.iconColor} ${status === 'processing' ? 'animate-spin' : ''}`}
            />
          </div>

          {/* Status Title */}
          <h2 className="text-xl font-heading font-semibold text-foreground text-center mb-2">
            {config.title}
          </h2>

          {/* Status Message */}
          <p className="text-muted-foreground text-center mb-6">
            {config.message}
          </p>

          {/* Transaction Details */}
          {(status === 'success' || status === 'failed') && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6 space-y-3">
              {amount && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className="font-medium text-foreground">₹{formatIndianNumber(amount)}</span>
                </div>
              )}
              {bookingReference && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Booking Reference</span>
                  <span className="font-data text-sm text-foreground">{bookingReference}</span>
                </div>
              )}
              {transactionId && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Transaction ID</span>
                  <span className="font-data text-sm text-foreground">{transactionId}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Date & Time</span>
                <span className="text-sm text-foreground">
                  {new Date().toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          )}

          {/* Processing Animation */}
          {status === 'processing' && (
            <div className="flex items-center justify-center space-x-2 mb-6">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-secondary rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <span className="text-sm text-muted-foreground">Processing...</span>
            </div>
          )}

          {/* Action Buttons */}
          {config.showActions && (
            <div className="space-y-3">
              {status === 'success' && (
                <>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    onClick={onClose}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Receipt
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={onClose}
                  >
                    View Booking Details
                  </Button>
                </>
              )}

              {status === 'failed' && (
                <>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    onClick={onRetry}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={onClose}
                  >
                    Contact Support
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Support Information */}
          {status === 'failed' && (
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Headphones" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Need Help?</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={12} />
                  <span>Call: 1800-123-4567 (Toll Free)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={12} />
                  <span>Email: support@drutravels.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MessageCircle" size={12} />
                  <span>Live Chat: Available 24/7</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusModal;